<ion-content style="--padding-top: 48px; --background: #f8fafc;">
    <div class="w-full max-w-4xl mx-auto p-4 pb-20">

        <!-- Loading -->
        <div *ngIf="isLoading" class="flex flex-col items-center justify-center h-96 gap-4">
            <ion-spinner name="crescent"></ion-spinner>
            <p class="text-gray-500">{{ 'vcaas.loading' | translate }}</p>
        </div>

        <div *ngIf="!isLoading">
            <!-- Header -->
            <div class="mb-8 flex items-center gap-4">
                <div class="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-md flex-shrink-0">
                    <i class="fa-solid fa-plug text-white text-xl"></i>
                </div>
                <div>
                    <h1 class="text-3xl font-extrabold text-slate-900 tracking-tight">{{ 'mcp.title' | translate }}</h1>
                    <p class="text-gray-500 mt-1 text-base">{{ 'mcp.subtitle' | translate }}</p>
                </div>
            </div>

            <!-- ═══════════════════════════════════════════════════ -->
            <!-- VALUE PROP — always visible                        -->
            <!-- ═══════════════════════════════════════════════════ -->
            <div class="mcp-value mb-6">
                <span class="mcp-value-eyebrow"><i class="fa-solid fa-wand-magic-sparkles"></i> {{ 'mcp.capabilitiesTitle' | translate }}</span>
                <h2 class="mcp-value-headline" [innerHTML]="'mcp.valueHeadline' | translate"></h2>
                <p class="mcp-value-lead">{{ 'mcp.valueLead' | translate }}</p>

                <!-- Two big highlights -->
                <div class="mcp-highlights">
                    <div class="mcp-highlight">
                        <div class="mcp-highlight-num">1<i class="fa-solid fa-arrow-right-long"></i>1000</div>
                        <div class="mcp-highlight-text">
                            <span class="mcp-highlight-title">{{ 'mcp.valueParallel' | translate }}</span>
                            <span class="mcp-highlight-desc">{{ 'mcp.valueParallelDesc' | translate }}</span>
                        </div>
                    </div>
                    <div class="mcp-highlight">
                        <div class="mcp-highlight-badge"><i class="fa-solid fa-plug-circle-check"></i></div>
                        <div class="mcp-highlight-text">
                            <span class="mcp-highlight-title">{{ 'mcp.valueNoDeps' | translate }}</span>
                            <span class="mcp-highlight-desc">{{ 'mcp.valueNoDepsDesc' | translate }}</span>
                        </div>
                    </div>
                </div>

                <!-- Everything included -->
                <p class="mcp-includes-title">{{ 'mcp.includesTitle' | translate }}</p>
                <div class="mcp-includes">
                    <div class="mcp-inc"><span class="mcp-inc-ico text-indigo-500"><i class="fa-solid fa-window-maximize"></i></span>{{ 'mcp.incFrontend' | translate }}</div>
                    <div class="mcp-inc"><span class="mcp-inc-ico text-violet-500"><i class="fa-solid fa-server"></i></span>{{ 'mcp.incBackend' | translate }}</div>
                    <div class="mcp-inc"><span class="mcp-inc-ico text-sky-500"><i class="fa-solid fa-database"></i></span>{{ 'mcp.incDatabase' | translate }}</div>
                    <div class="mcp-inc"><span class="mcp-inc-ico text-emerald-500"><i class="fa-solid fa-cloud-arrow-up"></i></span>{{ 'mcp.incStorage' | translate }}</div>
                    <div class="mcp-inc"><span class="mcp-inc-ico text-rose-500"><i class="fa-solid fa-envelope"></i></span>{{ 'mcp.incEmail' | translate }}</div>
                    <div class="mcp-inc"><span class="mcp-inc-ico text-orange-500"><i class="fa-solid fa-rocket"></i></span>{{ 'mcp.incHosting' | translate }}</div>
                </div>

                <a href="https://www.totalum.app/docs/mcp" target="_blank" rel="noopener"
                    class="mt-5 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
                    <i class="fa-solid fa-book text-xs"></i>
                    {{ 'mcp.seeMcpDocs' | translate }}
                    <i class="fa-solid fa-arrow-up-right-from-square text-[11px]"></i>
                </a>
            </div>

            <!-- Locked: needs credits -->
            <div *ngIf="apiKeyLocked" class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div class="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-5">
                    <div class="flex items-start gap-3">
                        <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
                            <i class="fa-solid fa-lock text-base"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <h3 class="font-bold text-slate-900 text-sm mb-1">{{ 'mcp.lockedTitle' | translate }}</h3>
                            <p class="text-xs text-gray-600 leading-relaxed mb-3">{{ 'mcp.lockedDesc' | translate }}</p>
                            <button type="button" (click)="goBuyCredits()"
                                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold shadow-sm hover:shadow-md transition-all"
                                style="background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);">
                                <i class="fa-solid fa-bolt text-[11px]"></i>
                                {{ 'mcp.lockedCta' | translate }}
                                <i class="fa-solid fa-arrow-right text-[11px]"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div *ngIf="!apiKeyLocked">
                <!-- Setup card -->
                <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
                        <div>
                            <h2 class="font-bold text-slate-900 text-lg">{{ 'mcp.setupTitle' | translate }}</h2>
                            <p class="text-xs text-gray-500 mt-0.5">OpenClaw, Claude Code, Codex, Cursor, Windsurf, Claude Desktop &amp; more</p>
                        </div>
                        <button
                            class="px-4 py-2 text-sm font-semibold rounded-xl transition-all flex items-center gap-2"
                            [ngClass]="mcpCopied ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'"
                            (click)="copyMcpSnippet()">
                            <i class="fa-solid" [ngClass]="mcpCopied ? 'fa-check' : 'fa-copy'"></i>
                            {{ mcpCopied ? ('vcaas.mcpCopied' | translate) : ('mcp.copyConfig' | translate) }}
                        </button>
                    </div>

                    <!-- Key controls: select which key + show/hide -->
                    <div class="mcp-key-bar mb-4">
                        <div class="flex items-center gap-2 min-w-0">
                            <i class="fa-solid fa-key text-indigo-400 flex-shrink-0"></i>
                            <span class="text-xs font-semibold text-slate-600 flex-shrink-0">{{ 'mcp.apiKey' | translate }}</span>
                            <div *ngIf="apiKeys.length > 1" class="relative">
                                <select class="mcp-key-select" [ngModel]="selectedKeyId" (ngModelChange)="onSelectKey($event)">
                                    <option *ngFor="let k of apiKeys" [value]="k._id">{{ k.name }}</option>
                                </select>
                            </div>
                            <span *ngIf="apiKeys.length <= 1 && selectedKey" class="text-xs text-slate-500 truncate">{{ selectedKey.name }}</span>
                        </div>
                        <button type="button" class="mcp-show-toggle" [class.active]="mcpShowFullKey" (click)="toggleShowKey()">
                            <i class="fa-solid" [ngClass]="mcpShowFullKey ? 'fa-eye-slash' : 'fa-eye'"></i>
                            {{ mcpShowFullKey ? ('mcp.hideKey' | translate) : ('mcp.showKey' | translate) }}
                        </button>
                    </div>
                    <p class="text-[11px] text-gray-400 mb-4 flex items-center gap-1.5">
                        <i class="fa-solid fa-shield-halved"></i>
                        {{ 'mcp.keyHiddenNote' | translate }}
                    </p>
                    <p class="text-[11px] text-gray-400 mb-4 flex items-center gap-1.5">
                        <i class="fa-solid fa-circle-info"></i>
                        {{ 'mcp.legacyUrlNote' | translate: { url: legacyMcpUrl } }}
                    </p>

                    <!-- Agent tabs -->
                    <div class="mcp-agent-tabs mb-4">
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'claude-code'" (click)="mcpActiveAgent = 'claude-code'">
                            <i class="fa-solid fa-terminal"></i> Claude Code
                        </button>
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'codex'" (click)="mcpActiveAgent = 'codex'">
                            <i class="fa-solid fa-robot"></i> Codex
                        </button>
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'cursor'" (click)="mcpActiveAgent = 'cursor'">
                            <i class="fa-solid fa-code"></i> Cursor
                        </button>
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'openclaw'" (click)="mcpActiveAgent = 'openclaw'">
                            <i class="fa-solid fa-paw"></i> OpenClaw
                        </button>
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'windsurf'" (click)="mcpActiveAgent = 'windsurf'">
                            <i class="fa-solid fa-wind"></i> Windsurf
                        </button>
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'claude-desktop'" (click)="mcpActiveAgent = 'claude-desktop'">
                            <i class="fa-solid fa-desktop"></i> Claude Desktop
                        </button>
                        <button class="mcp-agent-tab" [class.active]="mcpActiveAgent === 'generic'" (click)="mcpActiveAgent = 'generic'">
                            <i class="fa-solid fa-puzzle-piece"></i> Other
                        </button>
                    </div>

                    <!-- OpenClaw -->
                    <div *ngIf="mcpActiveAgent === 'openclaw'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-terminal mr-1.5"></i>Using the /mcp slash command (safe — preserves existing MCPs)</span>
                        </div>
                        <div class="mcp-snippet-body">
                            <code>/mcp set totalum-vcaas={{"{"}} "command": "npx", "args": ["-y", "mcp-remote", "{{ mcpUrl }}", "--header", "api-key: {{ mcpApiKeyDisplay }}"] {{"}"}}</code>
                        </div>
                        <div class="mcp-snippet-hint mb-3">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>Run this in any OpenClaw chat. Requires <code>commands.mcp: true</code> in your config. Use <code>/mcp show</code> to verify and <code>/mcp unset totalum-vcaas</code> to remove.</span>
                        </div>
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-file-code mr-1.5"></i>Or add to ~/.openclaw/openclaw.json under mcp.servers</span>
                        </div>
                        <div class="mcp-snippet-body">
<code>&#123;
  "mcp": &#123;
    "servers": &#123;
      "totalum-vcaas": &#123;
        "command": "npx",
        "args": [
          "-y", "mcp-remote",
          "{{ mcpUrl }}",
          "--header",
          "api-key: {{ mcpApiKeyDisplay }}"
        ]
      &#125;
    &#125;
  &#125;
&#125;</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>OpenClaw only supports stdio transport. The <code>mcp-remote</code> package bridges the Totalum HTTP server as a local process. Restart the gateway after editing the config file. Requires Node.js.</span>
                        </div>
                    </div>

                    <!-- Claude Code -->
                    <div *ngIf="mcpActiveAgent === 'claude-code'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-terminal mr-1.5"></i>Run in your terminal</span>
                        </div>
                        <div class="mcp-snippet-body">
                            <code>claude mcp add-json totalum-vcaas '{{"{"}}
  "type": "http",
  "url": "{{ mcpUrl }}",
  "headers": {{"{"}} "api-key": "{{ mcpApiKeyDisplay }}" {{"}"}}
{{"}"}}'</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>Or add it to <code>.mcp.json</code> in your project root for team sharing. Use <code>${{"{TOTALUM_API_KEY}"}}</code> instead of the key for env variable support.</span>
                        </div>
                    </div>

                    <!-- Codex -->
                    <div *ngIf="mcpActiveAgent === 'codex'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-terminal mr-1.5"></i>Run in your terminal (safe — preserves existing MCPs)</span>
                        </div>
                        <div class="mcp-snippet-body">
                            <code>codex mcp add totalum-vcaas --url {{ mcpUrl }}</code>
                        </div>
                        <div class="mcp-snippet-hint mb-3">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>Then add the <code>api-key</code> header to <code>~/.codex/config.toml</code> (the CLI does not support custom headers):</span>
                        </div>
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-file-code mr-1.5"></i>~/.codex/config.toml — add under [mcp_servers.totalum-vcaas]</span>
                        </div>
                        <div class="mcp-snippet-body">
<code>[mcp_servers.totalum-vcaas]
url = "{{ mcpUrl }}"
http_headers = &#123; "api-key" = "{{ mcpApiKeyDisplay }}" &#125;</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>For env variable support, replace <code>http_headers</code> with <code>env_http_headers = &#123; "api-key" = "TOTALUM_API_KEY" &#125;</code> and set <code>export TOTALUM_API_KEY="your_key"</code>.</span>
                        </div>
                    </div>

                    <!-- Cursor -->
                    <div *ngIf="mcpActiveAgent === 'cursor'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-file-code mr-1.5"></i>Add to ~/.cursor/mcp.json (or .cursor/mcp.json for project-level)</span>
                        </div>
                        <div class="mcp-snippet-body">
<code>&#123;
  "mcpServers": &#123;
    "totalum-vcaas": &#123;
      "url": "{{ mcpUrl }}",
      "headers": &#123;
        "api-key": "{{ mcpApiKeyDisplay }}"
      &#125;
    &#125;
  &#125;
&#125;</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>If you already have other MCP servers, just add the <code>"totalum-vcaas"</code> entry inside your existing <code>mcpServers</code> object. Restart Cursor after saving.</span>
                        </div>
                    </div>

                    <!-- Windsurf -->
                    <div *ngIf="mcpActiveAgent === 'windsurf'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-file-code mr-1.5"></i>Add to ~/.codeium/windsurf/mcp_config.json</span>
                        </div>
                        <div class="mcp-snippet-body">
<code>&#123;
  "mcpServers": &#123;
    "totalum-vcaas": &#123;
      "serverUrl": "{{ mcpUrl }}",
      "headers": &#123;
        "api-key": "{{ mcpApiKeyDisplay }}"
      &#125;
    &#125;
  &#125;
&#125;</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>If you already have other MCP servers, just add the <code>"totalum-vcaas"</code> entry inside your existing <code>mcpServers</code> object. Restart Windsurf completely after saving.</span>
                        </div>
                    </div>

                    <!-- Claude Desktop -->
                    <div *ngIf="mcpActiveAgent === 'claude-desktop'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-file-code mr-1.5"></i>Add to claude_desktop_config.json</span>
                        </div>
                        <div class="mcp-snippet-body">
<code>&#123;
  "mcpServers": &#123;
    "totalum-vcaas": &#123;
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "{{ mcpUrl }}",
        "--header",
        "api-key: {{ mcpApiKeyDisplay }}"
      ]
    &#125;
  &#125;
&#125;</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>If you already have other MCP servers, just add the <code>"totalum-vcaas"</code> entry inside your existing <code>mcpServers</code> object. Claude Desktop requires <code>mcp-remote</code> for HTTP servers. File location: macOS <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>, Linux <code>~/.config/Claude/claude_desktop_config.json</code>, Windows <code>%APPDATA%\Claude\claude_desktop_config.json</code>. Restart Claude Desktop after saving.</span>
                        </div>
                    </div>

                    <!-- Generic / Other -->
                    <div *ngIf="mcpActiveAgent === 'generic'" class="mcp-snippet-active">
                        <div class="mcp-snippet-header">
                            <span class="mcp-snippet-label"><i class="fa-solid fa-puzzle-piece mr-1.5"></i>Any MCP-compatible agent</span>
                        </div>
                        <div class="mcp-snippet-body">
<code>Server URL:   {{ mcpUrl }}
Transport:    Streamable HTTP
Header:       api-key: {{ mcpApiKeyDisplay }}

JSON config (most agents):
&#123;
  "mcpServers": &#123;
    "totalum-vcaas": &#123;
      "url": "{{ mcpUrl }}",
      "headers": &#123;
        "api-key": "{{ mcpApiKeyDisplay }}"
      &#125;
    &#125;
  &#125;
&#125;</code>
                        </div>
                        <div class="mcp-snippet-hint">
                            <i class="fa-solid fa-circle-info"></i>
                            <span>Use these values to configure any MCP-compatible agent. The server uses Streamable HTTP transport (not stdio). The <code>api-key</code> header must be sent with every request. Some agents use <code>serverUrl</code> instead of <code>url</code> — check your agent's documentation.</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ═══════════════════════════════════════════════════ -->
            <!-- EXAMPLE VIDEO                                       -->
            <!-- ═══════════════════════════════════════════════════ -->
            <div class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mt-6">
                <div class="flex items-center gap-3 mb-4">
                    <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-sm flex-shrink-0">
                        <i class="fa-solid fa-play text-white text-sm"></i>
                    </div>
                    <div>
                        <h2 class="font-bold text-slate-900 text-lg leading-tight">{{ 'mcp.videoTitle' | translate }}</h2>
                        <p class="text-xs text-gray-500 mt-0.5">{{ 'mcp.videoSubtitle' | translate }}</p>
                    </div>
                </div>

                <div class="mcp-video-note">
                    <i class="fa-solid fa-circle-info"></i>
                    <span>{{ 'mcp.videoDesc' | translate }}</span>
                </div>

                <div class="mcp-video">
                    <iframe
                        src="https://www.tella.tv/video/totalum-coding-as-a-service-explained-2s00/embed?b=0&title=0&a=1&loop=0&t=0&muted=0&wt=0"
                        allowfullscreen
                        allowtransparency
                        loading="lazy"
                        title="Totalum MCP example"></iframe>
                </div>
            </div>
        </div>
    </div>

    <!-- Fixed, always-visible "Need help?" contact CTA (shared component). -->
    <app-help-widget slot="fixed" style="bottom: 20px; right: 20px; z-index: 50;"></app-help-widget>
</ion-content>
